import { daysBetween, type IsoDate } from '../dates';
import type { Plan } from '../plan';
import type { Recipe } from '../recipe';
import { dayRules, relaxedCandidates } from './candidates';
import { consumptionCents } from './costs';
import { shoppingContext, type PlanRequest } from './request';

const VARIETY_DAYS = 6;

/**
 * DE: Verbrauchskosten einer Grundportion an einem Tag in Cent.
 * EN: Consumption cost of one base portion on a date in cents.
 */
export function portionCents(recipe: Recipe, date: IsoDate, request: PlanRequest): number {
  const total = consumptionCents(recipe, recipe.baseServings, shoppingContext(request), date);
  return total / recipe.baseServings;
}

function keepsVariety(plan: Plan, date: IsoDate, recipeId: string, request: PlanRequest): boolean {
  const days = [...(request.carryOver?.pastDays ?? []), ...plan.days];
  // DE: Der Tag selbst zählt mit, dadurch fällt auch das aktuelle Gericht weg.
  // EN: The day itself counts too, which also rules out the current dinner.
  return days.every(
    (other) =>
      other.dinner.recipeId !== recipeId || Math.abs(daysBetween(other.date, date)) > VARIETY_DAYS,
  );
}

/**
 * DE: Erlaubte Ersatzgerichte für einen Tag, das günstigste zuerst; die Abwechslungsregel gilt.
 * EN: Allowed replacement dinners for a day, cheapest first; the variety rule applies.
 */
export function dinnerAlternatives(plan: Plan, date: IsoDate, request: PlanRequest): Recipe[] {
  if (!plan.days.some((planned) => planned.date === date)) {
    throw new Error(`No planned dinner on ${date}`);
  }
  const { household, recipes, book } = request;
  const { recipes: candidates } = relaxedCandidates(
    recipes,
    household,
    book.catalog,
    dayRules(date, household),
  );
  return candidates
    .filter((recipe) => keepsVariety(plan, date, recipe.id, request))
    .map((recipe) => ({ recipe, cost: portionCents(recipe, date, request) }))
    .sort((a, b) => a.cost - b.cost || a.recipe.id.localeCompare(b.recipe.id))
    .map((candidate) => candidate.recipe);
}
